import { plainToClass } from "class-transformer";
import AuthorizationService from "../services/authorizationService";
import RepositoryBase from "./repositoryBase";
import EshopError from "../models/eshopError";
import EshopRequestBuilder from "../data/eshopRequestBuilder";
import EventBus from "../services/eventBus";
import ChargeOrderInstructionsResponse from "entities/chargeOrderInstructionsResponse";
import { formatISO } from "date-fns";
import ChargeValidityInstructionsResponse from "entities/chargeValidityInstructionsResponse";
import ChargeValidityInstructionGroupsResponse from "entities/chargeValidityInstructionGroupsResponse";
import ChargeOrderInstructionGroupsResponse from "entities/chargeOrderInstructionGroupsResponse";
import ChargeOrderInstructionRequest from "entities/chargeOrderInstructionRequest";
import ChargesRequest from "entities/chargesRequest";
import VehicleChargingInstructionsResponse from "../entities/vehicleChargingInstructionsResponse";
import { dateToIsoDate, dateToIsoString } from "utils/dateUtils";
import { utcToCZZonedTime } from "../utils/dateUtils";

export const GET_PROPOSED_VALIDITY_REQUEST_FAILURE = "GET_PROPOSED_VALIDITY_REQUEST_FAILURE";
export const GET_VEHICLE_INSTRUCTIONS_REQUEST_FAILURE = "GET_VEHICLE_INSTRUCTIONS_REQUEST_FAILURE";
export const GET_VEHICLE_REGISTRATIONS_REQUEST_FAILURE = "GET_VEHICLE_REGISTRATIONS_REQUEST_FAILURE";

export default class VehicleRepository extends RepositoryBase<EshopRequestBuilder> {
  constructor(
    protected apiFactory: ReturnType<typeof EshopRequestBuilder.Factory>,
    protected authorizationService: AuthorizationService,
    protected eventBus: EventBus
  ) {
    super(() => apiFactory(), authorizationService, eventBus);
  }

  getChargeOrderInstruction(countryId: string, licensePlate: string) {
    return this.callApi(api =>
      api
        .apiVersion(3)
        .one(`vehicles/${countryId}/${licensePlate}/charge_order_instructions`)
        .get<ChargeOrderInstructionsResponse>()
        .then(response => {
          return plainToClass(ChargeOrderInstructionsResponse, response);
        })
        .catch(() => new EshopError(GET_VEHICLE_REGISTRATIONS_REQUEST_FAILURE))
    );
  }

  getChargeOrderInstructions(charges: ChargeOrderInstructionRequest[]) {
    const postCharges = { charges: charges };
    return this.callApi(api =>
      this.apiFactory()
        .apiVersion(3)
        .one("vehicles/charge_order_instructions")
        .post<ChargeOrderInstructionRequest[] | EshopError>(postCharges)
        .then(response => {
          return plainToClass(ChargeOrderInstructionGroupsResponse, response);
        })
        .catch(() => new EshopError(GET_VEHICLE_REGISTRATIONS_REQUEST_FAILURE))
    );
  }

  getChargingInstruction(countryId: string, licensePlate: string) {
    return this.callApi(api =>
      api
        .apiVersion(3)
        .one(`vehicles/${countryId}/${licensePlate}/charging_instructions`)
        .get<VehicleChargingInstructionsResponse>()
        .then(response => {
          return plainToClass(VehicleChargingInstructionsResponse, response);
        })
        .catch(() => new EshopError(GET_VEHICLE_INSTRUCTIONS_REQUEST_FAILURE))
    );
  }

  getChargeValidityInstructions(charges: ChargesRequest[]) {
    const postCharges = { charges: charges };
    return this.callApi(api =>
      this.apiFactory()
        .apiVersion(3)
        .one("vehicles/charge_validity_instructions")
        .post<ChargesRequest[] | EshopError>(postCharges)
        .then(response => {
          return plainToClass(ChargeValidityInstructionGroupsResponse, response);
        })
        .catch(() => new EshopError(GET_VEHICLE_REGISTRATIONS_REQUEST_FAILURE))
    );
  }

  proposedValidity(
    countryId: string,
    licensePlate: string,
    priceListItemId?: string,
    hasImmediateValidity?: boolean,
    validSinceDate?: Date,
    validSinceDateString?: string,
    chargeId?: string
  ) {
    let reqObject: any = {};
    if (hasImmediateValidity && priceListItemId) {
      reqObject.has_immediate_validity = hasImmediateValidity;
      reqObject.price_list_item_id = priceListItemId;
    }
    if (!hasImmediateValidity && (validSinceDateString || validSinceDate) && priceListItemId) {
      const date = validSinceDateString ? validSinceDateString : validSinceDate ? dateToIsoDate(validSinceDate) : validSinceDate;
      reqObject.valid_since_date = date;
      reqObject.price_list_item_id = priceListItemId;
    }
    if (chargeId) {
      reqObject = { ...reqObject, charge_id: chargeId };
    }
    return this.callApi(api =>
      api
        .apiVersion(3)
        .one(`vehicles/${countryId}/${licensePlate}/charge_validity_instructions`)
        .clearGet(reqObject)
        .then(async response => {
          if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

          return plainToClass(ChargeValidityInstructionsResponse, (await response.json()) as ChargeValidityInstructionsResponse);
        })
        .catch(() => new EshopError(GET_PROPOSED_VALIDITY_REQUEST_FAILURE))
    );
  }
}
